import clsx from 'clsx';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductCard } from '@/components/Elements/ProductCard';
import { PATH_UPDATE_PAYMENT_METHOD } from '@/config';
import {
  ACTIVE,
  BILLING_MODE_MONTH,
  CANCELLED,
  INCOMPLETE,
  PENDING,
  PROCESSING,
} from '@/config/appConstants';
import { useMe } from '@/hooks/useMe';
import { BillingMode, Currency, Me, ProductTypes, UserProductTypes } from '@/types';
import {
  checkByStatus,
  filterUserProductsByStatus,
  getBillingModeFromUserProduct,
  getGuessURL,
  isExternalFreeProduct,
  isExternalPaidProduct,
  redirectToAndReload,
  userHasProducts,
} from '@/utils/helpers';

export type ProductCardsContainerProps = {
  className?: string;
  freemiumProduct: ProductTypes;
  productsCollection: ProductTypes[];
  userProducts?: UserProductTypes[];
  userCurrency: Currency;
  billingMode: BillingMode;
  onProductSelect: (product: ProductTypes) => void;
  onFreeProductSelect: (product: ProductTypes) => void;
  onRequestInfo: (product: ProductTypes) => void;
  userIsLogged?: boolean;
  showFeatures?: boolean;
  isSticky?: boolean;
};

export const ProductCardsContainer = ({
  className,
  freemiumProduct,
  productsCollection,
  userProducts,
  userCurrency,
  billingMode,
  onProductSelect,
  onFreeProductSelect,
  onRequestInfo,
  userIsLogged,
  showFeatures = true,
  isSticky = false,
}: ProductCardsContainerProps) => {
  const { getMe } = useMe();
  const me = getMe();

  const { t } = useTranslation();
  const onDefaultRequestInfo = (product: ProductTypes) => {
    onRequestInfo && onRequestInfo(product);
  };

  const setHideFree = (productsCollection: ProductTypes[]): boolean => {
    return (
      freemiumProduct &&
      productsCollection[0] &&
      productsCollection[0].info.productStep !== freemiumProduct.info.productStep
    );
  };

  const hideFree = setHideFree(productsCollection);

  const isHidePriceNeeded = (product: ProductTypes | null) => {
    if (product?.hidePrice) {
      return true;
    }

    if (!userProducts) {
      return false;
    }

    if (isExternalFreeProduct(userProducts)) {
      return false;
    }

    return isExternalPaidProduct(userProducts);
  };

  const isHideFreeProductNeeded = () =>
    userProducts ? isExternalPaidProduct(userProducts) : false;

  const getActionButtonLabel = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuySubscriptionProducts) {
      return t('ProductDetail.writeUs');
    }

    if ((userIsLogged && !userHasProducts(userProducts)) || processingProduct) {
      return t('ProductDetail.writeUs');
    }

    return t('ProductDetail.payNow');
  };

  const getOnActionButton = (me: Me) => {
    if (userIsLogged && !me.capabilities.canBuySubscriptionProducts) {
      return onDefaultRequestInfo;
    }

    if ((userIsLogged && !userHasProducts(userProducts)) || processingProduct) {
      return onDefaultRequestInfo;
    }

    return undefined;
  };

  const getFreeActionButtonLabel = () => {
    if (userIsLogged) {
      return t('ProductDetail.writeUs');
    }

    return undefined;
  };

  const userProductActive = filterUserProductsByStatus(userProducts, ACTIVE);
  const processingProduct = filterUserProductsByStatus(userProducts, PROCESSING);
  const userProductIncomplete = filterUserProductsByStatus(userProducts, INCOMPLETE);

  const activeBillingMode = userProductActive && getBillingModeFromUserProduct(userProductActive);

  const renderProduct = (product: ProductTypes) => {
    const productId = product.mdirectorId;
    const isCancelled = checkByStatus(userProducts, productId, [CANCELLED], billingMode);
    const isProcessing = checkByStatus(userProducts, productId, [PROCESSING], billingMode);
    const isPending = checkByStatus(userProducts, productId, [PENDING], billingMode);
    const isIncomplete = checkByStatus(userProducts, productId, [INCOMPLETE], billingMode);

    const isAlreadyPurchased = checkByStatus(
      userProducts,
      productId,
      [ACTIVE, CANCELLED, PROCESSING],
      billingMode
    );

    return (
      <Fragment key={product.id}>
        <ProductCard
          isSticky={isSticky}
          showFeatures={showFeatures}
          actionButtonLabel={getActionButtonLabel(me)}
          onActionButton={getOnActionButton(me)}
          product={product}
          hidePrice={isHidePriceNeeded(product)}
          userCurrency={userCurrency}
          onProductSelect={onProductSelect}
          onRequestInfo={onDefaultRequestInfo}
          userBillingMode={billingMode}
          activeBillingMode={activeBillingMode}
          alreadyPurchased={isAlreadyPurchased}
          alreadyPurchasedLink={getGuessURL()}
          isCancelled={isCancelled}
          isProcessing={isProcessing}
          isPending={isPending}
          isIncomplete={isIncomplete}
          onIncompleteAction={() => {
            redirectToAndReload(
              userProductIncomplete?.groupProduct.completePaymentUrl || PATH_UPDATE_PAYMENT_METHOD
            );
          }}
        />
      </Fragment>
    );
  };

  return (
    <div
      className={clsx(
        'md__product__collection m-auto overflow mt-0',
        {
          'lg:mt-9': !isSticky,
          'collection__comparator--mode relative left-0 lg:left-[115px]': !showFeatures,
        },
        className
      )}
    >
      <div className="flex items-center items-stretch flex-wrap">
        <div
          className={clsx('p-1 w-full md:w-1/2 xl:w-1/4 hidden lg:block', {
            'pt-8': !isSticky,
          })}
        >
          <ProductCard
            isSticky={isSticky}
            showFeatures={showFeatures}
            onActionButton={getOnActionButton(me)}
            actionButtonLabel={getFreeActionButtonLabel()}
            onProductSelect={onFreeProductSelect}
            onRequestInfo={onDefaultRequestInfo}
            product={freemiumProduct as ProductTypes}
            hidePrice={isHideFreeProductNeeded()}
            userCurrency={userCurrency}
            userBillingMode={BILLING_MODE_MONTH}
            activeBillingMode={BILLING_MODE_MONTH}
            className={clsx('overflow bg-md-white rounded-lg', { 'hidden lg:block': hideFree })}
            alreadyPurchased={checkByStatus(
              userProducts,
              freemiumProduct.mdirectorId,
              [ACTIVE, CANCELLED, PROCESSING],
              billingMode
            )}
            alreadyPurchasedLink={getGuessURL()}
          />
        </div>
        {productsCollection.map(renderProduct)}
      </div>
      {showFeatures && (
        <p className="text-right italic text-xs mt-14">{t('ProductDetail.taxesDisclaimer')}</p>
      )}
    </div>
  );
};
